// filepath: apps/web/src/features/tasks/useTasks.ts
//
// TanStack Query hook for the human-task inbox (FR-HT-1 / FR-UI-TASK-1).
//
// AC-1: "My Tasks" tab — GET /api/tasks/inbox?view=mine
// AC-2: "Unclaimed (my roles)" tab — GET /api/tasks/inbox?view=unclaimed
//
// INV-1: the query key includes tenantSlug so tasks for tenant A are
// never served from cache when the browser is scoped to tenant B.

import { useQuery } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";
import type { InboxResponse, InboxQuery } from "./types";
import { ApiError } from "@/definitions/types";

const TASKS_QUERY_KEY = "tasks" as const;

/**
 * TanStack Query key factory for task queries.
 * Colocated with the hook per react-conventions.instructions.md §TanStack Query Patterns.
 *
 * Note: tenantSlug is included in the key per INV-1, but since we rely on
 * the API URL path for tenant routing (not query params), we include it
 * for cache isolation hygiene even though the server uses the JWT.
 */
export const taskKeys = {
  inbox: (tenantSlug: string, view: InboxQuery["view"]) =>
    [TASKS_QUERY_KEY, tenantSlug, "inbox", view] as const,
};

/**
 * Fetch the task inbox.
 *
 * @param query.view  'mine' | 'unclaimed'
 * @param apiBase    Optional API base URL override (defaults to '/api').
 */
export function useTasks(
  { view }: { view: InboxQuery["view"] },
  apiBase = "/api",
): {
  tasks: InboxResponse["tasks"] | undefined;
  total: number | undefined;
  isLoading: boolean;
  isError: boolean;
  error: ApiError | null;
  refetch: () => Promise<unknown>;
} {
  const { tenantSlug } = useTenant();
  const query = useQuery<InboxResponse, ApiError>({
    queryKey: taskKeys.inbox(tenantSlug, view),
    queryFn: async () => {
      // Build the URL relative to window.location.origin. The previous
      // implementation passed `"http://localhost"` as the URL base, which
      // made the browser issue a cross-origin request to `localhost:80`
      // (no port) that always failed with CORS. The correct pattern is
      // a relative URL string — the browser resolves it against the
      // current origin (e.g. `http://localhost:5173`), and the vite
      // dev plugin proxies `/api/*` to the Go backend.
      //
      // For Node.js (test) callers that don't have a window.location,
      // we fall back to the absolute local origin. The relative path is
      // still resolvable in MSW via the `*/api/...` wildcard handler.
      const baseOrigin =
        typeof window !== "undefined"
          ? window.location.origin
          : "http://localhost";
      const url = new URL(`${apiBase}/tasks/inbox`, baseOrigin);
      url.searchParams.set("view", view);
      const response = await apiFetch(url.toString(), {
        init: { headers: { Accept: "application/json" } },
      });
      if (!response.ok) {
        const code = `http_${response.status}`;
        throw new ApiError(
          response.statusText || "fetch failed",
          response.status,
          code,
        );
      }
      return response.json() as Promise<InboxResponse>;
    },
    // No staleTime override — use default (no stale-while-revalidate for task lists).
    // Tasks are short-lived and should reflect server state quickly.
  });

  return {
    tasks: query.data?.tasks,
    total: query.data?.total,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
